import React, { useState } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import HairLineDivider from 'shared/components/vxl/divider/HairlineDivider/HairLineDivider';
import Icon from 'shared/components/vxl/icon/Icon/Icon';
import Tracker, { TrackerProps } from 'shared/components/vxl/Tracker/Tracker';
import BodyBaseBoldText from 'shared/components/vxl/typography/primary/body/BodyBaseBoldText';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import BodySmText from 'shared/components/vxl/typography/primary/body/BodySmText';
import BodyXSText from 'shared/components/vxl/typography/primary/body/BodyXSText';
import theme from 'shared/styles/theme';

import TrackerCardNudge, {
  TrackerCardNudgeProps,
} from './TrackerCardNudge/TrackerCardNudge';

export interface TrackerCardProps extends TrackerCardNudgeProps {
  /**
   * Title of the tracker card
   */
  titleText: string;
  /**
   * leadingLabel which indicates left side area information's label
   */
  leadingLabel?: string;
  /**
   * leadingText which indicates left side area information right below the leading label
   */
  leadingText?: string;
  /**
   * trailingLabel which indicates right side area information's label
   */
  trailingLabel?: string;
  /**
   * trailingText which indicates right side area information right below the trailingLabel label
   */
  trailingText?: string;
  /**
   * Click handler for tracker card top area section
   */
  cardOnPress?: () => void;
  /**
   * To display progress indicator on tracker card.
   * Note: provided background colors are overridden by background primary
   * the only color supported in this card.
   */
  trackerProps: TrackerProps;
}

const TrackerCard = ({
  titleText,
  cardOnPress,
  leadingLabel,
  leadingText,
  trailingLabel,
  trailingText,
  trackerProps,
  nudge,
}: TrackerCardProps) => {
  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isPressed, setIsPressed] = useState(false);

  const nudgeTitleText = nudge?.nudgeTitleText;

  return (
    <View style={styles.card}>
      <Pressable
        onHoverIn={() => setIsHovered(true)}
        onHoverOut={() => setIsHovered(false)}
        onBlur={() => setIsFocused(false)}
        onFocus={() => setIsFocused(true)}
        onPressIn={() => setIsPressed(true)}
        onPressOut={() => setIsPressed(false)}
        onPress={cardOnPress}
        style={[
          styles.trackerContainer,
          isFocused ? styles.focus : null,
          isHovered ? styles.hover : null,
          isPressed ? styles.press : null,
          !nudgeTitleText ? styles.topSectionMargins : null,
        ]}
        testID={titleText}
      >
        <View style={styles.header}>
          <BodySmText numberOfLines={1}>{titleText}</BodySmText>
          <View style={styles.iconWrapper}>
            <Icon
              iconAccessibilityLabel="borrowing-chevron-icon"
              iconKey="CHEVRON_RIGHT"
              iconSize="SM"
            />
          </View>
        </View>
        <View style={styles.progressContainer}>
          <Tracker
            {...{
              ...trackerProps,
              backgroundColor: theme.color.background.primary,
            }}
          />
        </View>
        <View style={styles.amountContainer}>
          <View>
            {leadingLabel ? (
              <BodyXSText numberOfLines={1}>{leadingLabel}</BodyXSText>
            ) : null}
            {leadingText ? (
              <BodyBaseBoldText numberOfLines={1}>
                {leadingText}
              </BodyBaseBoldText>
            ) : null}
          </View>
          <View>
            {trailingLabel ? (
              <BodyXSText numberOfLines={1}>{trailingLabel}</BodyXSText>
            ) : null}
            {trailingText ? (
              <BodyBaseText style={styles.trailingTextStyle} numberOfLines={1}>
                {trailingText}
              </BodyBaseText>
            ) : null}
          </View>
        </View>
        {nudgeTitleText ? (
          <View style={styles.dividerContainer}>
            <HairLineDivider />
          </View>
        ) : null}
      </Pressable>
      <TrackerCardNudge nudge={nudge} />
    </View>
  );
};

const styles = StyleSheet.create({
  amountContainer: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  card: {
    backgroundColor: theme.color.interactive.tertiary,
    borderRadius: 4,
    width: '100%',
  },
  dividerContainer: {
    height: 1,
    marginTop: theme.spacing.m,
  },
  focus: {
    outlineColor: theme.color.interactive.focused.border,
  },
  header: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  hover: {
    backgroundColor: theme.color.interactive.tertiaryHovered,
  },
  iconWrapper: {
    marginTop: theme.spacing.xs,
  },
  press: {
    backgroundColor: theme.color.interactive.tertiaryPressed,
  },
  progressContainer: {
    borderRadius: 4,
    height: 10,
    marginBottom: theme.spacing.m,
    marginTop: theme.spacing.s,
  },
  topSectionMargins: {
    borderBottomLeftRadius: 4,
    borderBottomRightRadius: 4,
    paddingBottom: theme.spacing.m,
  },
  trackerContainer: {
    borderTopLeftRadius: 4,
    borderTopRightRadius: 4,
    paddingHorizontal: theme.spacing.m,
    paddingTop: theme.spacing.m,
    width: '100%',
  },
  trailingTextStyle: {
    color: theme.color.text.secondary,
  },
});

export default TrackerCard;
